
'use client';

import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Leaf, Recycle, Sun, Menu, X } from 'lucide-react';
import {
  Card,
  CardContent,
  CardTitle,
} from '@/components/ui/card';
import { useState } from 'react';

export function EcoFriendlyHomepage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="bg-emerald-50 text-gray-700 font-sans">
      <header className="container mx-auto p-6 flex justify-between items-center bg-emerald-50/80 backdrop-blur-md sticky top-0 z-10">
        <h2 className="font-semibold text-2xl text-emerald-800">Verdant</h2>
        <nav className="hidden md:flex gap-8 items-center">
          <a href="#" className="hover:text-emerald-900 transition-colors">
            Our Mission
          </a>
          <a href="#" className="hover:text-emerald-900 transition-colors">
            Products
          </a>
          <a href="#" className="hover:text-emerald-900 transition-colors">
            Journal
          </a>
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full transition-colors">
            Shop Sustainably
          </Button>
        </nav>
        <div className="md:hidden">
            <Button size="icon" variant="ghost" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                {isMenuOpen ? <X/> : <Menu/>}
            </Button>
        </div>
      </header>
      
      {isMenuOpen && (
          <div className="md:hidden bg-emerald-100">
              <nav className="container mx-auto flex flex-col items-start gap-4 p-6">
                <a href="#" className="text-lg hover:text-emerald-900 transition-colors">Our Mission</a>
                <a href="#" className="text-lg hover:text-emerald-900 transition-colors">Products</a>
                <a href="#" className="text-lg hover:text-emerald-900 transition-colors">Journal</a>
                <Button className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full transition-colors w-full mt-2">
                    Shop Sustainably
                </Button>
              </nav>
          </div>
      )}

      <main>
        <section className="relative min-h-[85vh] flex items-center py-12">
          <div className="container mx-auto grid md:grid-cols-2 gap-12 items-center z-10 px-4">
            <div className="text-center md:text-left">
              <h1 className="text-5xl md:text-6xl font-serif text-emerald-900 leading-snug">
                Rooted in Nature,
                <br />
                Crafted for You.
              </h1>
              <p className="mt-4 text-lg text-emerald-700 max-w-md mx-auto md:mx-0">
                Discover ethically sourced goods that are kind to you and the
                planet.
              </p>
              <Button
                size="lg"
                className="mt-8 bg-white text-emerald-800 border-2 border-emerald-800 hover:bg-emerald-100 rounded-full px-8 shadow-sm hover:shadow-md transition-shadow"
              >
                Explore the Collection
              </Button>
            </div>
            <div className="relative h-64 w-64 md:h-96 md:w-96 mx-auto">
              <Image
                src="https://picsum.photos/seed/eco5/600/600"
                alt="Sunlight filtering through lush green leaves"
                fill
                className="rounded-full shadow-lg object-cover"
                data-ai-hint="green leaves"
              />
            </div>
          </div>
        </section>

        <section className="py-24 bg-white px-4">
          <div className="container mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl font-serif text-emerald-900 mb-4">
                Our Promise
              </h2>
              <p className="text-lg text-emerald-700">
                We are committed to sustainability at every step, from sourcing
                materials to shipping our products. Our goal is to leave the
                world better than we found it.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-12 text-center">
              <div>
                <div className="flex justify-center mb-4">
                  <Leaf className="w-10 h-10 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-emerald-800 mb-2">
                  Natural Materials
                </h3>
                <p className="text-emerald-600">
                  We use only organic, biodegradable, and renewable materials
                  in all our products.
                </p>
              </div>
              <div>
                <div className="flex justify-center mb-4">
                  <Recycle className="w-10 h-10 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-emerald-800 mb-2">
                  Recycled Packaging
                </h3>
                <p className="text-emerald-600">
                  Our packaging is 100% post-consumer recycled and is fully
                  recyclable itself.
                </p>
              </div>
              <div>
                <div className="flex justify-center mb-4">
                  <Sun className="w-10 h-10 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-emerald-800 mb-2">
                  Ethical Production
                </h3>
                <p className="text-emerald-600">
                  We partner with suppliers who share our values of fair labor
                  and environmental care.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-emerald-50 px-4">
          <div className="container mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl font-serif text-emerald-900 mb-4">
                Featured Products
              </h2>
              <p className="text-lg text-emerald-700">
                Handpicked favorites, loved by our community.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8">
              <Card>
                <div className="p-0">
                  <Image
                    src="https://picsum.photos/seed/eco-prod1/600/400"
                    width={600}
                    height={400}
                    alt="Reusable bamboo cutlery set"
                    className="rounded-t-lg"
                    data-ai-hint="bamboo cutlery"
                  />
                </div>
                <CardContent className="p-6">
                  <CardTitle className="text-xl text-emerald-800">
                    Traveler's Cutlery Set
                  </CardTitle>
                  <p className="text-emerald-600 mt-2">$24.00</p>
                </CardContent>
              </Card>
              <Card>
                <div className="p-0">
                  <Image
                    src="https://picsum.photos/seed/eco-prod2/600/400"
                    width={600}
                    height={400}
                    alt="Organic cotton tote bag"
                    className="rounded-t-lg"
                    data-ai-hint="cotton tote"
                  />
                </div>
                <CardContent className="p-6">
                  <CardTitle className="text-xl text-emerald-800">
                    Organic Market Tote
                  </CardTitle>
                  <p className="text-emerald-600 mt-2">$35.00</p>
                </CardContent>
              </Card>
              <Card className="sm:col-span-2 md:col-span-1">
                <div className="p-0">
                  <Image
                    src="https://picsum.photos/seed/eco-prod3/600/400"
                    width={600}
                    height={400}
                    alt="Set of beeswax food wraps"
                    className="rounded-t-lg"
                    data-ai-hint="beeswax wraps"
                  />
                </div>
                <CardContent className="p-6">
                  <CardTitle className="text-xl text-emerald-800">
                    Beeswax Food Wraps
                  </CardTitle>
                  <p className="text-emerald-600 mt-2">$18.00</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>
      <footer className="bg-emerald-800 text-emerald-50 py-16 px-4">
        <div className="container mx-auto grid md:grid-cols-3 gap-8">
          <div>
            <h3 className="font-bold text-lg mb-4">Verdant</h3>
            <p className="text-sm text-emerald-200">
              Sustainable goods for a conscious life.
            </p>
          </div>
          <div>
            <h3 className="font-bold text-lg mb-4">Explore</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Shop
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Our Story
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  FAQ
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-bold text-lg mb-4">Stay Connected</h3>
            <p className="text-sm text-emerald-200 mb-4">
              Join our newsletter for updates and special offers.
            </p>
            <div className="flex">
              <input
                type="email"
                placeholder="Your email"
                className="w-full rounded-l-full px-4 py-2 text-gray-800"
              />
              <Button className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-r-full">
                Subscribe
              </Button>
            </div>
          </div>
        </div>
        <div className="container mx-auto text-center mt-12 border-t border-emerald-700 pt-8 text-sm text-emerald-200">
          <p>
            &copy; {new Date().getFullYear()} Verdant Living. All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
